/**
 * Hook to power @-mention document selection in the chat input.
 * Detects "@" at a word boundary, fetches catalog search results with
 * debounce, and manages keyboard navigation + selection state.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { searchCatalogIndex, useCatalogIndexSnapshot } from "./useCatalogIndex";
import { apiGet } from "../utils/apiClient";

// ── Types ──────────────────────────────────────────────────────────────

export interface CatalogDocument {
	doc_id: string;
	filename: string;
	document_type: string;
	entity_name: string | null;
	period_end_date: string | null;
	deal_name: string;
	ref_type?: "document" | "deal" | "template";
	project_id?: string | null;
	/** Set only on synthetic template rows (ref_type === "template"). */
	template_id?: string;
	/** The uploaded file behind a document row; absent on deal rows. */
	attachment_id?: number | null;
}

export interface MentionTemplateOption {
	id: string;
	name: string;
	description: string;
	/** Lowercase tokens that make this template match a query. */
	keywords?: string[];
}

interface UseDocumentMentionOptions {
	/** Templates surfaced at the top of the dropdown, keyboard-navigable. */
	templates?: MentionTemplateOption[];
	/** Called when a template row is chosen (mouse or keyboard). */
	onTemplateSelect?: (templateId: string) => void;
}

export interface UseDocumentMentionReturn {
	isMentionActive: boolean;
	mentionResults: CatalogDocument[];
	isMentionLoading: boolean;
	selectedDocs: CatalogDocument[];
	highlightedIndex: number;
	handleInputChange: (
		value: string,
		cursorPos: number,
	) => string;
	handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>, currentInput: string) => boolean | string;
	selectDocument: (doc: CatalogDocument, currentInput?: string) => string | undefined;
	removeDocument: (docId: string) => void;
	clearSelection: () => void;
	dismissMention: () => void;
	prefetchMentionResults: () => void;
}

// ── Constants ──────────────────────────────────────────────────────────

const DEBOUNCE_MS = 250;
const RESULT_LIMIT = 15;

// ── Hook ───────────────────────────────────────────────────────────────

export const useDocumentMention = (
	options: UseDocumentMentionOptions = {},
): UseDocumentMentionReturn => {
	const catalogIndex = useCatalogIndexSnapshot();
	// Kept in refs so the race-sensitive fetch/commit closures always read the
	// latest templates + callback without being part of their dep arrays.
	const templatesRef = useRef<MentionTemplateOption[]>(options.templates ?? []);
	const onTemplateSelectRef = useRef<
		((templateId: string) => void) | undefined
	>(options.onTemplateSelect);
	templatesRef.current = options.templates ?? [];
	onTemplateSelectRef.current = options.onTemplateSelect;

	// Synthetic template rows matching the current query, prepended to results
	// so they participate in keyboard navigation (highlight, arrow up/down,
	// Enter) exactly like document rows.
	const buildTemplateDocs = useCallback((query: string): CatalogDocument[] => {
		const q = query.trim().toLowerCase();
		return templatesRef.current
			.filter(
				(t) =>
					q === "" ||
					t.name.toLowerCase().includes(q) ||
					(t.keywords ?? []).some((k) => k.includes(q) || q.includes(k)),
			)
			.map((t) => ({
				doc_id: `template::${t.id}`,
				filename: t.name,
				document_type: "",
				entity_name: null,
				period_end_date: null,
				deal_name: t.description,
				ref_type: "template" as const,
				template_id: t.id,
			}));
	}, []);

	const withTemplates = useCallback(
		(query: string, docs: CatalogDocument[]): CatalogDocument[] => [
			...buildTemplateDocs(query),
			...docs.filter((d) => d.ref_type !== "template"),
		],
		[buildTemplateDocs],
	);
	const [isMentionActive, setIsMentionActive] = useState(false);
	const [mentionQuery, setMentionQuery] = useState("");
	const [mentionResults, setMentionResults] = useState<CatalogDocument[]>([]);
	const [isMentionLoading, setIsMentionLoading] = useState(false);
	const [selectedDocs, setSelectedDocs] = useState<CatalogDocument[]>([]);
	const [highlightedIndex, setHighlightedIndex] = useState(0);
	const [mentionStartIndex, setMentionStartIndex] = useState(-1);
	const [mentionEndIndex, setMentionEndIndex] = useState(-1);

	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isMountedRef = useRef(true);
	const mentionResultsLengthRef = useRef(0);
	// Monotonic request id. Bumped on every query change, dismissal, and
	// active-state change. A fetch only commits its response if its captured
	// id still matches — so late arrivals from stale requests are dropped.
	const requestIdRef = useRef(0);
	// In-flight requests keyed by normalized query. Lets a prefetch and a
	// debounced keystroke for the same key share one round trip instead of
	// racing two identical requests on the server fallback path.
	const inFlightRef = useRef<Map<string, Promise<CatalogDocument[]>>>(new Map());

	useEffect(() => {
		mentionResultsLengthRef.current = mentionResults.length;
	}, [mentionResults.length]);

	// ── Fetch catalog search results ───────────────────────────────────

	const fetchResults = useCallback(
		async (query: string, requestId: number) => {
			const normalizedQuery = query.trim();

			const isCurrent = () =>
				isMountedRef.current && requestIdRef.current === requestId;

			try {
				const encoded = encodeURIComponent(normalizedQuery);
				const endpoint = normalizedQuery
					? `/api/v3/catalog/mention-search?q=${encoded}&limit=${RESULT_LIMIT}`
					: `/api/v3/catalog/mention-search?limit=${RESULT_LIMIT}`;
				// Reuse an in-flight request for the same key if one exists,
				// so the prefetch + first-`@` race becomes a single fetch.
				let pending = inFlightRef.current.get(normalizedQuery);
				if (!pending) {
					pending = apiGet<CatalogDocument[]>(endpoint).finally(() => {
						inFlightRef.current.delete(normalizedQuery);
					});
					inFlightRef.current.set(normalizedQuery, pending);
				}
				const results = await pending;

				// Only the freshest response for a query is allowed to write
				// the cache. Otherwise a late response for the same normalized
				// query could overwrite a newer cached value with stale data
				// (e.g. @ac → @aci → @ac, where the first @ac response lands
				// after the second).
				if (isCurrent()) {
					setMentionResults(withTemplates(query, results));
					setHighlightedIndex(0);
					setIsMentionLoading(false);
				}
			} catch (err) {
				console.warn("Catalog search failed:", err);
				if (isCurrent()) {
					setMentionResults(withTemplates(query, []));
					setIsMentionLoading(false);
				}
			}
		},
		[withTemplates],
	);

	// ── Debounced search trigger ───────────────────────────────────────

	useEffect(() => {
		if (!isMentionActive) {
			// Bump the request id so any in-flight fetch from a prior
			// activation drops its response on arrival.
			requestIdRef.current += 1;
			setMentionResults([]);
			setIsMentionLoading(false);
			return;
		}

		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}

		if (catalogIndex.status === "ready" && catalogIndex.index) {
			// Bump the request id so any in-flight server fetch from a prior
			// keystroke (started while the index was still loading/oversize)
			// drops its response on arrival instead of overwriting the local
			// results we're about to render.
			requestIdRef.current += 1;
			setMentionResults(
				withTemplates(
					mentionQuery,
					searchCatalogIndex(
						catalogIndex.docs,
						catalogIndex.index,
						mentionQuery,
						RESULT_LIMIT,
					),
				),
			);
			setHighlightedIndex(0);
			setIsMentionLoading(false);
			return;
		}

		// Every query change invalidates prior in-flight fetches.
		const myId = ++requestIdRef.current;
		const normalized = mentionQuery.trim();

		// Empty query = the user just typed `@` and we want results
		// instantly. Anything else is live typing and gets debounced so
		// we don't hammer the endpoint per keystroke.
		if (normalized === "") {
			setIsMentionLoading(mentionResultsLengthRef.current === 0);
			fetchResults(mentionQuery, myId);
			return;
		}

		setIsMentionLoading(mentionResultsLengthRef.current === 0);

		debounceRef.current = setTimeout(() => {
			fetchResults(mentionQuery, myId);
		}, DEBOUNCE_MS);

		return () => {
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, [
		mentionQuery,
		isMentionActive,
		fetchResults,
		withTemplates,
		catalogIndex.status,
		catalogIndex.index,
		catalogIndex.docs,
		catalogIndex.version,
	]);

	// ── Cleanup on unmount ─────────────────────────────────────────────

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (debounceRef.current) {
				clearTimeout(debounceRef.current);
			}
		};
	}, []);

	// ── Dismiss mention mode ───────────────────────────────────────────

	const dismissMention = useCallback(() => {
		// Invalidate any in-flight fetch so its late response can't repopulate
		// results after the user dismissed.
		requestIdRef.current += 1;
		if (debounceRef.current) {
			clearTimeout(debounceRef.current);
			debounceRef.current = null;
		}
		setIsMentionActive(false);
		setMentionQuery("");
		setMentionResults([]);
		setIsMentionLoading(false);
		setHighlightedIndex(0);
		setMentionStartIndex(-1);
		setMentionEndIndex(-1);
	}, []);

	// ── Select a document ──────────────────────────────────────────────

	const selectDocument = useCallback(
		(doc: CatalogDocument, currentInput?: string): string | undefined => {
			// Template rows are actions, not attachments: strip the @query, close
			// the dropdown, and hand off to the template callback (e.g. open the
			// schema-extractor form). They are never added as document chips.
			if (doc.ref_type === "template") {
				let cleanedTemplate: string | undefined;
				if (currentInput !== undefined && mentionStartIndex >= 0) {
					const end =
						mentionEndIndex >= 0 && mentionEndIndex <= currentInput.length
							? mentionEndIndex
							: currentInput.length;
					cleanedTemplate =
						currentInput.slice(0, mentionStartIndex) +
						currentInput.slice(end);
					cleanedTemplate =
						cleanedTemplate.trimStart() === "" ? "" : cleanedTemplate;
				}
				dismissMention();
				onTemplateSelectRef.current?.(
					doc.template_id ?? doc.doc_id.replace(/^template::/, ""),
				);
				return cleanedTemplate;
			}

			// Avoid duplicates
			setSelectedDocs((prev) => {
				if (prev.some((d) => d.doc_id === doc.doc_id)) return prev;
				return [...prev, doc];
			});

			// Remove the @query text from the input. The mention span runs
			// from the @ to the tracked end (cursor position at last input
			// change) — this allows queries containing spaces without
			// swallowing arbitrary trailing text.
			let cleaned: string | undefined;
			if (currentInput !== undefined && mentionStartIndex >= 0) {
				const end =
					mentionEndIndex >= 0 && mentionEndIndex <= currentInput.length
						? mentionEndIndex
						: currentInput.length;
				cleaned =
					currentInput.slice(0, mentionStartIndex) +
					currentInput.slice(end);
				cleaned = cleaned.trimStart() === "" ? "" : cleaned;
			}

			dismissMention();
			return cleaned;
		},
		[dismissMention, mentionStartIndex, mentionEndIndex],
	);

	// ── Handle input changes — detect @ trigger ────────────────────────

	const handleInputChange = useCallback(
		(value: string, cursorPos: number): string => {
			// Scan backward from the cursor for the nearest word-boundary `@`
			// on the current line. This re-activates the mention when the user
			// backspaces into an existing @token or moves the cursor into one,
			// and keeps it active across spaces so multi-word filenames are
			// searchable. Mentions do not cross newlines.
			let atIdx = -1;
			for (let i = cursorPos - 1; i >= 0; i--) {
				const ch = value[i];
				if (ch === "\n") break;
				if (ch === "@") {
					const prev = i > 0 ? value[i - 1] : undefined;
					if (
						prev === undefined ||
						prev === " " ||
						prev === "\n" ||
						prev === "\t"
					) {
						atIdx = i;
					}
					break;
				}
			}

			if (atIdx >= 0) {
				if (!isMentionActive || mentionStartIndex !== atIdx) {
					setIsMentionActive(true);
					setMentionStartIndex(atIdx);
					setHighlightedIndex(0);
				}
				setMentionQuery(value.slice(atIdx + 1, cursorPos));
				setMentionEndIndex(cursorPos);
				return value;
			}

			if (isMentionActive) {
				dismissMention();
			}
			return value;
		},
		[isMentionActive, mentionStartIndex, dismissMention],
	);

	// ── Handle keyboard navigation in the mention dropdown ─────────────

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLTextAreaElement>, currentInput: string): boolean | string => {
			if (!isMentionActive) return false;

			switch (e.key) {
				case "ArrowDown":
					e.preventDefault();
					setHighlightedIndex((prev) =>
						prev < mentionResults.length - 1 ? prev + 1 : 0,
					);
					return true;

				case "ArrowUp":
					e.preventDefault();
					setHighlightedIndex((prev) =>
						prev > 0 ? prev - 1 : mentionResults.length - 1,
					);
					return true;

				case "Enter":
				case "Tab":
					if (mentionResults.length > 0) {
						e.preventDefault();
						const cleaned = selectDocument(mentionResults[highlightedIndex], currentInput);
						return cleaned !== undefined ? cleaned : true;
					}
					return false;

				case "Escape":
					e.preventDefault();
					dismissMention();
					return true;

				default:
					return false;
			}
		},
		[
			isMentionActive,
			mentionResults,
			highlightedIndex,
			selectDocument,
			dismissMention,
		],
	);

	// ── Remove a previously selected document ──────────────────────────

	const removeDocument = useCallback((docId: string) => {
		setSelectedDocs((prev) => prev.filter((d) => d.doc_id !== docId));
	}, []);

	// ── Clear all selections ───────────────────────────────────────────

	const clearSelection = useCallback(() => {
		setSelectedDocs([]);
	}, []);

	// ── Prefetch empty-query results into cache ───────────────────────

	const prefetchMentionResults = useCallback(async () => {
		await catalogIndex.prefetch();
	}, [catalogIndex]);

	return {
		isMentionActive,
		mentionResults,
		isMentionLoading,
		selectedDocs,
		highlightedIndex,
		handleInputChange,
		handleKeyDown,
		selectDocument,
		removeDocument,
		clearSelection,
		dismissMention,
		prefetchMentionResults,
	};
};
